import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    faArrowRightFromBracket,
    faArrowRightToBracket,
    faCheckCircle,
    faCloudArrowDown,
    faComment,
    faEdit,
    faInbox,
    faLock,
    faRightLeft,
    faRotateForward,
    faRotateRight,
    faUnlock,
    faUserCheck,
    faUserXmark,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { Disclosure } from "@headlessui/react";
import moment from "moment-timezone";
import Modal from "../../../../components/Modal";
import { renderUserName } from "../../../../utils/helpers/user";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../../utils/hooks/company";
import { groupPayPeriodLogs, summarizePayPeriod } from "./historyGroups";
import { formatChange, formatHours, formatLedgerDate, startedBeforePlan } from "./ledger";

// icon and colour of a timeline node, per kind of log
const HISTORY_KINDS = {
    assign: { icon: faUserCheck, tone: "success" },
    transfer: { icon: faArrowRightToBracket, tone: "success" },
    "transfer-unassign": { icon: faArrowRightFromBracket, tone: "danger" },
    unassign: { icon: faUserXmark, tone: "danger" },
    "lock-pay-group": { icon: faLock, tone: "primary" },
    "pay-period-calc": { icon: faLock, tone: "primary" },
    "unlock-pay-group": { icon: faUnlock, tone: "warning" },
    "update-total": { icon: faEdit, tone: "purple" },
    "regenerate-from-year-change": { icon: faRotateRight, tone: "info" },
    "close-current-year": { icon: faRotateForward, tone: "dark" },
    "update-from-integration": { icon: faCloudArrowDown, tone: "info" },
    "transfer-adjustment": { icon: faRightLeft, tone: "warning" },
};

const historyKind = (item) => {
    if (item?.type === "transfer-adjustment" && item.meta?.transferAdjustment?.rejected) {
        return { icon: faRightLeft, tone: "muted" };
    }
    return HISTORY_KINDS[item?.type] || { icon: faEdit, tone: "muted" };
};

const renderPlanName = (timeoff) =>
    timeoff?.description ? `${timeoff.code} (${timeoff.description})` : timeoff?.code;

// The date a transfer takes effect, as a calendar date (no time, no timezone shift).
// log.effectiveDate is not reliable for older transfers (apisync eligibility, backfilled logs
// stored the write time there), so prefer the start of the plan the balance moved into.
const transferEffectiveDate = (planStartDate, item, dateFormat) => {
    if (planStartDate) {
        const date = moment(planStartDate, "YYYY-MM-DD", true);
        if (date.isValid()) return date.format(dateFormat);
    }
    if (item?.effectiveDate) {
        const date = moment.parseZone(item.effectiveDate);
        if (date.isValid()) return date.format(dateFormat);
    }
    return null;
};

const HistoryContent = ({ plan, filters, toolbar }) => {
    const { t } = useTranslation();

    const dateFormat = useCompanyDateFormat();

    const timeFormat = useCompanyTimeFormat();

    // useEffect(() => {
    //     const controller = new AbortController();
    //     if (planId) {
    //         const params = {
    //             plan: planId,
    //             year: filters.year,
    //             start: filters?.from && moment(filters.from).format("YYYY-MM-DD"),
    //             end: filters?.to && moment(filters.to).format("YYYY-MM-DD"),
    //             type: filters?.type,
    //         };
    //         const url = isSelf ? "/profile/time-off-history" : `/users/${user?.id}/time-off-history/`;

    //         getHistory(url, params, controller);
    //     }
    //     return () => controller.abort();
    // }, [getHistory, planId, isSelf, user?.id, filters.year, filters?.from, filters?.to, filters?.type]);

    // the plan's first pay period can start before the plan: it is counted on the plan active on its last day
    const renderStartedBeforePlan = useCallback(
        (item) => {
            const periodStart = item.payGroupItemStartDate && moment.parseZone(item.payGroupItemStartDate).format("YYYY-MM-DD");
            return startedBeforePlan(periodStart, plan?.startDate) ? (
                <small className="text-muted ml-1">
                    ({t("pay-period-started-before-plan", { date: formatLedgerDate(plan.startDate, dateFormat) })})
                </small>
            ) : null;
        },
        [plan?.startDate, t, dateFormat],
    );

    const renderHistoryTypes = useCallback(
        (item) => {
            switch (item.type) {
                // case "approve-request": {
                //     return (
                //         <div>
                //             <FontAwesomeIcon icon={faCheckCircle} className="mr-1 mt-1 text-green" />
                //             {t("approved-request", {
                //                 user: renderUserName(item.user),
                //                 hours: -item.diff,
                //             })}{" "}
                //         </div>
                //     );
                // }
                // case "approve-cancel-request": {
                //     return (
                //         <>
                //             <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green" />
                //             {t("approved-cancel-request", {
                //                 hours: Number(item.diff).toFixed(2),
                //             })}{" "}
                //         </>
                //     );
                // }
                case "assign": {
                    const assignMessage = t("assigned-this-plan", {
                        user: renderUserName(item.user),
                        hours: Number(item.diff.initial).toFixed(2),
                    });
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: assignMessage }} />
                        </>
                    );
                }
                case "transfer": {
                    // older transfers did not record which plan the balance came from
                    const from = item.meta?.transferPlan?.timeoff;
                    // this log sits on the new plan, so the transfer takes effect on this plan's start
                    const date = transferEffectiveDate(plan?.startDate, item, dateFormat);
                    const key = from?.code ? "transfer-this-plan" : "transfer-this-plan-unknown";
                    const transferMessage = t(date ? `${key}-effective` : key, {
                        user: renderUserName(item.user),
                        hours: Number(item.diff.initial).toFixed(2),
                        plan: renderPlanName(from),
                        date,
                    });
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: transferMessage }} />
                            {item.comment && <span className="text-muted"> - <FontAwesomeIcon icon={faComment} className="mr-2 text-default" />{item.comment}</span>}
                        </>
                    );
                }
                case "transfer-unassign": {
                    const to = item.meta?.transferToPlan;
                    // this log sits on the old plan; the transfer takes effect on the new plan's start
                    const date = transferEffectiveDate(to?.plan?.startDate, item, dateFormat);
                    const key = to?.timeoff?.code ? "transfer-unassign-this-plan" : "transfer-unassign-this-plan-unknown";
                    const transferUnassignMessage = t(date ? `${key}-effective` : key, {
                        user: renderUserName(item.user),
                        hours: Number(to?.plan?.bucket?.initial ?? -(item.diff?.updates || 0)).toFixed(2),
                        plan: renderPlanName(to?.timeoff),
                        date,
                    });
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: transferUnassignMessage }} />
                            {item.comment && <span className="text-muted"> - <FontAwesomeIcon icon={faComment} className="mr-2 text-default" />{item.comment}</span>}
                        </>
                    );
                }
                case "payout": {
                    const hours = Number(
                        Math.abs(
                            item.diff?.available ??
                            item.diff?.consumed ??
                            ((item.old?.available ?? 0) - (item.new?.available ?? 0)),
                        ),
                    ).toFixed(2);
                    const payoutMessage = t("paid-out-this-plan", {
                        user: renderUserName(item.user),
                        hours,
                    });
                    return (
                        <>
                            <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green" />
                            <span dangerouslySetInnerHTML={{ __html: payoutMessage }} />
                            {item.comment && <span className="text-muted"> - <FontAwesomeIcon icon={faComment} className="mr-2 text-default" />{item.comment}</span>}
                        </>
                    );
                }
                case "unassign": {
                    const unassignMessage = t("unassigned-this-plan", {
                        user: renderUserName(item.user),
                        hours: Number(item.diff.updates).toFixed(2),
                    });
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: unassignMessage }} />
                        </>
                    );
                }
                case "lock-pay-group": {
                    let message;
                    if (Number(item.diff.earned) === 0 && Number(item.diff.lost) > 0) {
                        if (!!(item.payGroupItemStartDate && item.payGroupItemEndDate)) {
                            message = t("locked-pay-group-with-dates-lost", {
                                user: renderUserName(item.user),
                                payGroupCode: item.payGroupCode,
                                amount: Number(item.diff.earned).toFixed(2),
                                consumed: Number(item.diff.consumed).toFixed(2),
                                startDate: moment.parseZone(item.payGroupItemStartDate).format(dateFormat),
                                endDate: moment.parseZone(item.payGroupItemEndDate).format(dateFormat),
                            })
                        } else {
                            message = t("locked-pay-group-lost", {
                                user: renderUserName(item.user),
                                payGroupCode: item.payGroupCode,
                                amount: Number(item.diff.earned).toFixed(2),
                                consumed: Number(item.diff.consumed).toFixed(2),
                            })
                        }
                    } else if (!!(item.payGroupItemStartDate && item.payGroupItemEndDate)) {
                        message = t("locked-pay-group-with-dates", {
                            user: renderUserName(item.user),
                            payGroupCode: item.payGroupCode,
                            amount: Number(item.diff.earned).toFixed(2),
                            consumed: Number(item.diff.consumed).toFixed(2),
                            startDate: moment.parseZone(item.payGroupItemStartDate).format(dateFormat),
                            endDate: moment.parseZone(item.payGroupItemEndDate).format(dateFormat),
                        })
                    } else {
                        message = t("locked-pay-group", {
                            user: renderUserName(item.user),
                            payGroupCode: item.payGroupCode,
                            amount: Number(item.diff.earned).toFixed(2),
                            consumed: Number(item.diff.consumed).toFixed(2),
                        })
                    }
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: message }} />
                            {renderStartedBeforePlan(item)}
                        </>
                    );
                }
                case "unlock-pay-group": {
                    let message;
                    if (!!(item.payGroupItemStartDate && item.payGroupItemEndDate)) {
                        message = t("unlocked-pay-group-with-dates", {
                            user: renderUserName(item.user),
                            payGroupCode: item.payGroupCode,
                            amount: Number(item.diff.earned).toFixed(2),
                            consumed: Number(Math.abs(item.diff.consumed)).toFixed(2),
                            startDate: moment.parseZone(item.payGroupItemStartDate).format(dateFormat),
                            endDate: moment.parseZone(item.payGroupItemEndDate).format(dateFormat),
                        })
                    } else {
                        message = t("unlocked-pay-group", {
                            user: renderUserName(item.user),
                            payGroupCode: item.payGroupCode,
                            amount: Number(item.diff.earned).toFixed(2),
                            consumed: Number(Math.abs(item.diff.consumed)).toFixed(2),
                        })
                    }
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: message }} />
                            {renderStartedBeforePlan(item)}
                        </>
                    );
                }
                case "update-total": {
                    const updateMessage = t("updated-total", {
                        user: renderUserName(item.user),
                        added: t(item.diff.updates > 0 ? "added" : "removed"),
                        update: Number(Math.abs(item.diff.updates).toFixed(2)),
                        oldTotal: Number(item.old.total).toFixed(2),
                        newTotal: Number(item.new.total).toFixed(2),
                    });
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: updateMessage }} />
                            {item.comment && <span className="text-muted"> - <FontAwesomeIcon icon={faComment} className="mr-2 text-default" />{item.comment}</span>}
                        </>
                    );
                }
                case "regenerate-from-year-change": {
                    const regenerateMessage = t("regenerate-from-year-change", {
                        amount: Number(item.diff.initial).toFixed(2),
                    });
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: regenerateMessage }} />
                        </>
                    );
                }
                case "transfer-assign": {
                    return (
                        <>
                            {t("transfer-assign-message", {
                                user: renderUserName(item.user),
                                plan: item?.transfer?.plan?.code,
                                amount: item?.transfer?.total
                            })}
                        </>
                    )
                }
                case "transfer-unassign": {
                    return (
                        <>
                            {t("transfer-unassign-message", {
                                user: renderUserName(item.user),
                                plan: item?.transfer?.plan?.code,
                                amount: item?.transfer?.total
                            })}
                        </>
                    )
                }
                case "close-current-year": {
                    const closeMessage = t("close-current-year", {
                        amount: (-1 * item.diff.updates).toFixed(2),
                    });
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: closeMessage }} />
                        </>
                    );
                }
                case "update-from-integration": {
                    let message = t("update-from-integration", {
                        amount: Number(item.diff.earned).toFixed(2),
                        consumed: Number(item.diff.consumed).toFixed(2),
                    })
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: message }} />
                        </>
                    );
                }
                case "transfer-adjustment": {
                    // a pay period before a transfer changed and was passed on down the chain (settings
                    // PtoTransferProvenanceService): the old plan gives `residual`, the next one receives `carry`
                    const adjustment = item.meta?.transferAdjustment || {};
                    const signed = (value) => `${Number(value) > 0 ? "+" : ""}${Number(value || 0).toFixed(2)}`;
                    // logs written before the decision was recorded: read it from their comment
                    const decision = adjustment.rejected
                        ? "rejected"
                        : adjustment.decision
                          || (/^Approved change/.test(item.comment || "") ? "approved" : /auto-approved/.test(item.comment || "") ? "backfill" : "auto");
                    const incoming = !!adjustment.from;
                    const plan = incoming
                        ? adjustment.fromPlanCode || t("history-adjustment-previous-plan")
                        : adjustment.toPlanCode || t("history-adjustment-next-plan");
                    const main = t(decision === "rejected" ? "history-adjustment-rejected" : `history-adjustment-${decision}-${incoming ? "in" : "out"}`, {
                        user: renderUserName(item.user),
                        hours: signed(incoming ? item.diff?.initial ?? adjustment.carry : adjustment.residual),
                        plan,
                    });
                    const periods = (adjustment.periods || [])
                        .map((period) => `${formatLedgerDate(period.periodStartDate, dateFormat)} - ${formatLedgerDate(period.periodEndDate, dateFormat)}`)
                        .join(", ");
                    const reason = periods && adjustment.originPlanCode
                        ? t("history-adjustment-reason-periods", { periods, origin: adjustment.originPlanCode })
                        : adjustment.originPlanCode
                          ? t("history-adjustment-reason-origin", { origin: adjustment.originPlanCode })
                          : t("history-adjustment-reason");
                    // the system's own comments repeat the reason; only a comment someone typed is shown
                    const typedComment = item.comment && !/^(Approved change|Change|Rejected change) of a pay period before the transfer/.test(item.comment)
                        ? item.comment
                        : null;
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: `${main}: ${reason}` }} />
                            {typedComment && <span className="text-muted"> - <FontAwesomeIcon icon={faComment} className="mr-2 text-default" />{typedComment}</span>}
                        </>
                    );
                }
                default: {
                    // any other entry still says who changed the balance and by how much, never a blank card
                    const diff = item.diff || {};
                    const change = (diff.initial || 0) + (diff.earned || 0) + (diff.updates || 0) - (diff.consumed || 0);
                    const message = t("history-other-change", {
                        user: renderUserName(item.user),
                        hours: `${change > 0 ? "+" : ""}${change.toFixed(2)}`,
                        type: item.type || "",
                    });
                    return (
                        <>
                            <span dangerouslySetInnerHTML={{ __html: message }} />
                            {item.comment && <span className="text-muted"> - <FontAwesomeIcon icon={faComment} className="mr-2 text-default" />{item.comment}</span>}
                        </>
                    );
                }
            }
        },
        [dateFormat, plan?.startDate, renderStartedBeforePlan, t]
    );

    // a timeline of pay periods: one node per pay period (its net effect, from its logs) and one
    // node per other event; the period's full chronology opens in a modal
    const entries = useMemo(() => groupPayPeriodLogs(plan?.logs), [plan?.logs]);

    const [chronology, setChronology] = useState(null);
    const closeChronology = useCallback(() => setChronology(null), []);

    const formatWhen = (value) => {
        const date = moment.parseZone(value);
        return date.isValid() ? `${date.local().format(dateFormat)} ${date.local().format(timeFormat)}` : "";
    };

    const userName = (user) => (user ? renderUserName(user) : t("history-system"));

    const renderMarker = ({ icon, tone }) => (
        <span className={`history-timeline-marker history-tone-${tone}`} aria-hidden="true">
            <FontAwesomeIcon icon={icon} fixedWidth />
        </span>
    );

    // any log as a timeline node with its descriptive text (other events, and the chronology modal)
    const renderEventNode = (item, key) => (
        <li key={key} className="history-timeline-item">
            {renderMarker(historyKind(item))}
            <div className="history-timeline-body history-timeline-event">
                <div className="history-timeline-text">{renderHistoryTypes(item)}</div>
                <div className="history-timeline-meta">
                    <time dateTime={item.date}>{formatWhen(item.date)}</time>
                </div>
            </div>
        </li>
    );

    const renderPeriodNode = (group, key) => {
        const summary = summarizePayPeriod(group);
        const { latest, locked, relocks } = summary;
        const figures = [
            { value: summary.accrued, label: "history-period-accrued", tone: "success" },
            { value: -summary.used, label: "history-period-used", tone: "danger" },
            { value: -summary.lost, label: "history-period-lost", tone: "warning" },
        ].filter(({ value }) => formatChange(value) !== "0.00");
        const status = !locked
            ? t("history-period-unlocked-by", { user: userName(latest.user), date: formatWhen(latest.date) })
            : relocks > 0
              ? t("history-period-relocked", { count: relocks, user: userName(latest.user), date: formatWhen(latest.date) })
              : t("history-period-locked-by", { user: userName(latest.user), date: formatWhen(latest.date) });

        return (
            <li key={key} className="history-timeline-item">
                {renderMarker(locked ? { icon: faLock, tone: "primary" } : { icon: faUnlock, tone: "warning" })}
                <div className="history-timeline-body history-timeline-period">
                    <div className="history-timeline-head">
                        <span className="history-timeline-title">
                            {formatLedgerDate(latest.payGroupItemStartDate, dateFormat)}
                            {" – "}
                            {formatLedgerDate(latest.payGroupItemEndDate, dateFormat)}
                        </span>
                        {latest.payGroupCode && <span className="badge badge-secondary">{latest.payGroupCode}</span>}
                        {!locked && <span className="badge badge-warning">{t("unlocked")}</span>}
                        {summary.balance !== null && (
                            <span className="history-timeline-balance">
                                {t("balance")} <strong>{formatHours(summary.balance)}</strong>
                            </span>
                        )}
                    </div>
                    {renderStartedBeforePlan(latest)}
                    <div className="history-timeline-figures">
                        {figures.length > 0 ? (
                            figures.map(({ value, label, tone }) => (
                                <span key={label} className={`history-figure history-tone-${tone}`}>
                                    <strong>{formatChange(value)}</strong> {t(label)}
                                </span>
                            ))
                        ) : (
                            <span className="text-muted">{t("history-period-no-change")}</span>
                        )}
                    </div>
                    <div className="history-timeline-meta">
                        <span>{status}</span>
                        {group.length > 1 && (
                            <button
                                type="button"
                                className="history-group-badge"
                                aria-haspopup="dialog"
                                onClick={() => setChronology(group)}
                            >
                                {t("pay-period-other-logs", { count: group.length - 1 })}
                            </button>
                        )}
                    </div>
                </div>
            </li>
        );
    };

    const chronologyLog = chronology?.[0];

    return (
        <Disclosure.Panel key={plan.id} className="history-accordion-panel">
            {toolbar}
            { plan?.logs?.length > 0 ? (
                <ol className="history-timeline">
                    {entries.map(({ log: item, group }, i) =>
                        group ? renderPeriodNode(group, i) : renderEventNode(item, i)
                    )}
                </ol>
            ) : (
                <div className="d-flex h-100 text-gray flex-column align-items-center justify-content-center">
                    <FontAwesomeIcon icon={faInbox} size="2xl" />
                    <span>{t("no-data")}</span>
                </div>
            )}

            <Modal
                isOpen={!!chronology}
                toggle={closeChronology}
                size="lg"
                scrollable
                title={(
                    <div className="d-flex align-items-center justify-content-between gap-2">
                        <span>
                            {t("pay-period-chronology-title", {
                                startDate: formatLedgerDate(chronologyLog?.payGroupItemStartDate, dateFormat),
                                endDate: formatLedgerDate(chronologyLog?.payGroupItemEndDate, dateFormat),
                            })}
                        </span>
                        <button type="button" className="btn btn-link p-0 text-default" aria-label={t("close")} onClick={closeChronology}>
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </div>
                )}
            >
                <ol className="history-timeline">
                    {chronology?.map((item, i) => renderEventNode(item, i))}
                </ol>
            </Modal>
        </Disclosure.Panel>
    );
};

export default HistoryContent;
